import type { Timezone } from 'shared/lib/timezones';
import type { PromoStatusModel, PromoTypes } from 'entities/contractor';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ResourceSuccessApiResponse } from 'shared/lib/api';
import type { Column } from 'shared/ui/Table';
import type { SalesRep } from 'entities/sales-rep';
import type { CampaignStatusModel, CampaignTypeKey, ScheduleDayModel } from 'entities/campaign';
import type { BadgeProps } from 'shared/ui/Badge';
import type { SettingType } from 'entities/campaign-groups';

export interface ListRowsValueProps {
  data: string[];
}

export interface SchedulingUpdateCellProps {
  data: SchedulingUpdateModel;
}

export type SeveralRowsValueProps = {
  data: HistoryCampaignData | HistoryPromoData | HistoryContractorData | HistoryCrmData | HistoryInvoiceData;
  wrapperClass?: string;
};

export type InvoiceMultipleRowOldNewProps = {
  data: Partial<InvoiceRegeneratedBody>;
};

export type OldToNewSeveralRowsProps = {
  data: FiltersAppliedEventData['body'];
};

export type CampaignHistoryEventTypes =
  | 'campaign_created'
  | 'campaign_status_updated'
  | 'campaign_name_updated'
  | 'campaign_budget_updated'
  | 'campaign_calendar_url_updated'
  | 'campaign_minimal_income_updated'
  | 'campaign_target_area_updated'
  | 'campaign_bid_updated'
  | 'campaign_schedule_updated'
  | 'integration_created'
  | 'integration_updated'
  | 'integration_deleted'
  | 'test_crm_request'
  | 'campaign_is_active_updated'
  | 'campaign_pausing_reason_updated';

export type CampaignHistoryData<TEvent extends CampaignHistoryEventTypes, TBody> = {
  eventType: TEvent;
  body: TBody;
};

export type HistoryPromoCreatedBody = {
  contractor: {
    contractor_id: number;
    company_name: string;
  };
  promo: {
    type: keyof typeof PromoTypes;
    service: string;
    status: PromoStatusModel;
    launch_budget: number;
    budget: number;
    spent_launch_budget: number;
  };
};

export type HistoryPromoStartedBody = {
  contractor: {
    contractor_id: number;
    company_name: string;
  };
  promo: {
    contractor: number;
    type: keyof typeof PromoTypes;
    service: string;
    status: PromoStatusModel;
    launch_budget: number;
    budget: number;
    spent_launch_budget: number;
    spent_budget: number;
  };
};

export type HistoryPromoFinishedBody = {
  contractor: {
    contractor_id: number;
    company_name: string;
  };
  promo: {
    contractor: number;
    type: keyof typeof PromoTypes;
    service: string;
    status: PromoStatusModel;
    launch_budget: number;
    budget: number;
    spent_budget: number;
  };
};

type LeadOrAppointmentHistoryData<TEvent extends LeadOrAppointmentHistoryEventTypes, TBody> = {
  eventType: TEvent;
  body: TBody;
};

type LeadOrAppointmentCreatedEventData = LeadOrAppointmentHistoryData<
  'lead_billable',
  {
    old: boolean;
    new: boolean;
    reason?: string;
    reasonId?: number;
    reasonDescription?: string | null;
  }
>;

type LeadOrAppointmentHistoryCommonProperties = {
  type: 'lead';
  objectId: number;
  createdAt: string;
  actor: string;
  source: LogsSource;
};

type LeadOrAppointmentAssignedEventData = LeadOrAppointmentHistoryData<'lead_assigned', LeadOrAppointmentHistoryBody>;

export type LeadOrAppointmentHistoryModel = LeadOrAppointmentHistoryCommonProperties &
  (LeadOrAppointmentCreatedEventData | LeadOrAppointmentAssignedEventData);

type InvoiceHistoryCommonProperties = {
  type: 'invoice';
  objectId: number;
  createdAt: string;
  actor: string;
  source: LogsSource;
};

type InvoiceHistoryData<TEvent extends InvoiceHistoryEventTypes, TBody> = {
  eventType: TEvent;
  body: TBody;
};

type InvoiceChargeFailedEventData = InvoiceHistoryData<
  'invoice_charge_failed',
  {
    invoiceNumber: string;
    contractor: string;
    reason: string;
    paymentMethod: string;
  }
>;

type InvoiceChargeCreatedEventData = InvoiceHistoryData<
  'invoice_created',
  {
    invoiceNumber: string;
    contractor: string;
    items: number;
    refundItems: number;
    discount: string;
    total: string;
  }
>;

type InvoiceStatusUpdatedEventData = InvoiceHistoryData<
  'invoice_status_updated',
  {
    old: string;
    new: string;
  }
>;

type InvoiceRegeneratedBody = {
  items: {
    old: number;
    new: number;
  };
  refundItems: {
    old: number;
    new: number;
  };
  discount: {
    old: string;
    new: string;
  };
  total: {
    old: string;
    new: string;
  };
};

type InvoiceRegeneratedEventData = InvoiceHistoryData<'invoice_regenerated', InvoiceRegeneratedBody>;

type InvoiceUpdatedEventData = InvoiceHistoryData<'invoice_updated', Pick<InvoiceRegeneratedBody, 'items' | 'total'>>;

type InvoiceChargeSuccessfulEventData = InvoiceHistoryData<
  'invoice_charge_successful',
  {
    invoiceNumber: string;
    contractor: string;
    paymentMethod: string;
  }
>;

export type InvoiceHistoryModel = InvoiceHistoryCommonProperties &
  (
    | InvoiceChargeFailedEventData
    | InvoiceChargeCreatedEventData
    | InvoiceStatusUpdatedEventData
    | InvoiceRegeneratedEventData
    | InvoiceUpdatedEventData
    | InvoiceChargeSuccessfulEventData
  );

export enum HistoryContractorRowKey {
  fullName = 'Full Name',
  companyName = 'Company',
  phoneNumber = 'Phone',
  email = 'Email',
  website = 'Website',
  timezone = 'Time Zone',
  manager = 'Partnerships Manager',
  salesRep = 'Sales Rep',
  paymentMethod = 'Payment Method',
}

type PromoHistoryEventTypes = 'promo_created' | 'promo_started' | 'promo_finished';
type CreditCardHistoryEventTypes = 'credit_card_created' | 'credit_card_updated' | 'credit_card_deleted';
export type ContractorHistoryEventTypes =
  | 'contractor_created'
  | 'contractor_company_updated'
  | 'contractor_email_updated'
  | 'contractor_phone_updated'
  | 'contractor_billing_emails_added'
  | 'contractor_billing_emails_deleted'
  | 'contractor_website_updated'
  | 'contractor_time_zone_updated'
  | 'contractor_account_manager_updated'
  | 'contractor_payment_method_updated'
  | 'contractor_password_updated'
  | 'contractor_full_name_updated'
  | 'contractor_sales_rep_updated'
  | 'holiday_added'
  | 'holiday_deleted'
  | PromoHistoryEventTypes
  | CreditCardHistoryEventTypes;

export type CrmHistoryEventTypes =
  | 'crm_request_sent_to_queue'
  | 'crm_response_positive'
  | 'crm_response_failed'
  | 'crm_response_negative';

export type InvoiceHistoryEventTypes =
  | 'invoice_charge_failed'
  | 'invoice_created'
  | 'invoice_status_updated'
  | 'invoice_updated'
  | 'invoice_regenerated'
  | 'invoice_charge_successful';

type CrmHistoryData<TEvent extends CrmHistoryEventTypes, TBody> = {
  eventType: TEvent;
  body: TBody;
};

type CrmRequestSentToQueueEventData = CrmHistoryData<
  'crm_request_sent_to_queue',
  {
    integrationAlias: string;
  }
>;

type CrmResponsePositiveEventData = CrmHistoryData<
  'crm_response_positive',
  {
    statusCode: number;
    integrationAlias: string;
  }
>;

type CrmResponseFailedEventData = CrmHistoryData<
  'crm_response_failed',
  {
    integrationAlias: string;
  }
>;

type CrmResponseNegativeEventData = CrmHistoryData<
  'crm_response_negative',
  {
    statusCode: number;
    integrationAlias: string;
  }
>;

type TestCrmRequestEventData = Omit<CrmHistoryCommonProperties, 'type'> &
  CampaignHistoryCommonProperties & {
    eventType: 'test_crm_request';
    body: {
      integrationAlias: string;
      status: keyof typeof CrmHistoryRowValue;
      statusCode?: number;
    };
  };

type CrmHistoryCommonProperties = {
  type: 'lead-crm';
  historyId: number;
  createdAt: string;
  isDownloadable: boolean;
};

export type CrmHistoryModel = CrmHistoryCommonProperties &
  (
    | CrmRequestSentToQueueEventData
    | CrmResponsePositiveEventData
    | CrmResponseFailedEventData
    | CrmResponseNegativeEventData
  );

export enum CrmHistoryRowKey {
  statusCode = 'Status code',
  integrationAlias = 'Integration',
  status = 'Status',
}

export const CrmHistoryRowValue: Record<string, string> = {
  sent: 'Sent',
  notSent: 'Not Sent',
  failed: 'Failed',
};

export type HistoryCrmData = Partial<Record<keyof typeof CrmHistoryRowKey, string | number>>;

export enum InvoiceHistoryRowKey {
  invoiceNumber = 'Invoice #',
  contractor = 'Contractor',
  reason = 'Reason',
  paymentMethod = 'Payment method',
  items = 'Items',
  refundItems = 'Refund items',
  discount = 'Discount',
  total = 'Total',
}

export type HistoryInvoiceData = Partial<Record<keyof typeof InvoiceHistoryRowKey, string | number>>;

export type HistoryCampaignData = Partial<
  Record<keyof typeof HistoryCampaignRowKey, string | number | boolean | number[]>
>;

export enum HistoryCampaignRowKey {
  name = 'Name',
  contractor = 'Contractor',
  type = 'Type',
  zip_codes = 'ZIP Codes',
  service = 'Service',
  status = 'Status',
  is_active = 'Is active',
  bids = 'Bids',
  weekly_budget = 'Weekly Budget',
}

export type HistoryModels =
  | ContractorHistoryModel
  | LeadOrAppointmentHistoryModel
  | CampaignHistoryModel
  | CrmHistoryModel
  | GroupHistoryModel
  | InvoiceHistoryModel;

export type HistoryFetcher = () => UseQueryResult<ResourceSuccessApiResponse<HistoryModels[]>>;
export type HistoryTableColumns = Column<HistoryModels>[];

export enum HistoryPromoRowKey {
  company_name = 'Contractor',
  type = 'Type',
  service = 'Service',
  status = 'Status',
  launch_budget = 'Launch Budget',
  spent_launch_budget = 'Already spent to launch a Promo',
  budget = 'Promo Budget',
  spent_budget = 'Spent Budget',
}

export type HistoryPromoData = HistoryPromoCreatedBody | HistoryPromoStartedBody | HistoryPromoFinishedBody;
export type HistoryContractorData = Partial<Record<keyof typeof HistoryContractorRowKey, string | SalesRep | null>>;

export type LeadOrAppointmentHistoryEventTypes = 'lead_assigned' | 'lead_billable';

type StepCampaigns = {
  id: number;
  name: string;
};

export interface LeadOrAppointmentHistoryBody {
  leadId: number;
  zipCode: string;
  serviceName: string;
  leadCreatedAt: string;
  leadAssignedAt: string;
  algorithmVersion: number;
  winnerId: number;
  winnerName: string;
  reasonId?: boolean;
  reason?: string;
  sumOfLeads?: number;
  serviceTier?: null | string;
  stepZeroCampaigns?: StepCampaigns[];
  stepOneCampaigns?: StepCampaigns[];
  stepTwoCampaigns?: StepCampaigns[];
  stepThreeCampaigns?: StepCampaigns[];
}

export enum HistoryAssignedEventGeneralInfoKey {
  leadId = 'Lead ID',
  zipCode = 'ZIP Code',
  sumOfLeads = 'Leads came to competing campaigns that day',
  serviceName = 'Service',
  serviceTier = 'Service Tier',
  leadCreatedAt = 'Created at',
  leadAssignedAt = 'Assigned at',
  algorithmVersion = 'Lead distribution algorithm version',
  winnerName = 'Winning Campaign',
}

export enum HistoryAssignedEventStepsInfoKey {
  stepThreeCampaigns = 'Step 3',
  stepTwoCampaigns = 'Step 2',
  stepOneCampaigns = 'Step 1',
  stepZeroCampaigns = 'Step 0',
}

export type CampaignCreatedEventData = CampaignHistoryData<
  'campaign_created',
  {
    name: string;
    contractor: number;
    type: CampaignTypeKey;
    zip_codes: number;
    service: string;
    status: CampaignStatusModel;
    is_active: boolean;
    bids: number | number[];
    weekly_budget: string;
  }
>;

export type CampaignStatusUpdatedEventData = CampaignHistoryData<
  'campaign_status_updated',
  {
    old: CampaignStatusModel;
    new: CampaignStatusModel;
  }
>;

export interface SchedulingUpdateDayModel {
  old: ScheduleDayModel | null;
  new: ScheduleDayModel;
}

type ChangeViewsConfig = {
  OldToNewValueText: {
    viewType: 'OldToNewValueText';
    oldValue?: string | null;
    newValue?: string | null;
    showInTwoLines?: boolean;
  };
  OldToNewValueTextCapitalized: {
    viewType: 'OldToNewValueTextCapitalized';
    oldValue?: string | null;
    newValue?: string | null;
    showInTwoLines?: boolean;
  };
  OldToNewValuePausingReasons: {
    viewType: 'OldToNewValuePausingReasons';
    oldValue: string;
    newValue: string;
  };
  OldToNewValueNumber: { viewType: 'OldToNewValueNumber'; oldValue: number; newValue: number };
  OldToNewValueBadge: { viewType: 'OldToNewValueBadge'; oldValue: BadgeProps; newValue: BadgeProps };
  OldToNewValueMoney: { viewType: 'OldToNewValueMoney'; oldValue: number; newValue: number };
  OldToNewValueMoneyDate: {
    viewType: 'OldToNewValueMoneyDate';
    oldValue: number;
    newValue: number;
    effectiveAt: string;
  };
  OldToNewValueIsActive: { viewType: 'OldToNewValueIsActive'; oldValue: boolean; newValue: boolean };
  OldToNewValueBidOrBidTiers: {
    viewType: 'OldToNewValueBidOrBidTiers';
    oldValue: number | number[];
    newValue: number | number[];
  };
  OldToNewValueOnOff: {
    viewType: 'OldToNewValueOnOff';
    oldValue: boolean;
    newValue: boolean;
    reasonId?: number;
    reason?: string;
    reasonDescription?: string | null;
  };
  OldToNewValueIsApplied: { viewType: 'OldToNewValueIsApplied'; oldValue: boolean; newValue: boolean };
  OldToNewValueTrafficDays: { viewType: 'OldToNewValueTrafficDays'; oldValue: string; newValue: string };
  OldToNewSeveralRows: {
    viewType: 'OldToNewSeveralRows';
    data: OldToNewSeveralRowsProps['data'];
  };
  CardEventCreated: { viewType: 'CardEventCreated'; isDefault: boolean; last4: string };
  CardEventDeleted: { viewType: 'CardEventDeleted'; isDefault: undefined; last4: string };
  CardEventUpdated: {
    viewType: 'CardEventUpdated';
    isDefault: {
      old: boolean;
      new: boolean;
    };
    last4: string;
  };
  ListRows: { viewType: 'ListRows'; data: ListRowsValueProps['data'] };
  SchedulingUpdateCell: { viewType: 'SchedulingUpdateCell'; data: SchedulingUpdateCellProps['data'] };
  SeveralRowsValue: { viewType: 'SeveralRowsValue'; data: SeveralRowsValueProps['data'] };
  SeveralRowsValueOverflow: { viewType: 'SeveralRowsValueOverflow'; data: SeveralRowsValueProps['data'] };
  AssignedEvent: { viewType: 'AssignedEvent'; data: LeadOrAppointmentHistoryBody };
  InvoiceMultipleRowOldNewValues: {
    viewType: 'InvoiceMultipleRowOldNewValues';
    data: InvoiceMultipleRowOldNewProps['data'];
  };
  IntegrationEventCreated: { viewType: 'IntegrationEventCreated'; isActive: boolean };
  IntegrationEventUpdated: { viewType: 'IntegrationEventUpdated'; isActive: boolean };
  IntegrationEventDeleted: { viewType: 'IntegrationEventDeleted'; label: string };
  EmptyView: { viewType: 'EmptyView' };
};
export type ChangeViews = ChangeViewsConfig[keyof ChangeViewsConfig];

type CampaignNameUpdatedEventData = CampaignHistoryData<
  'campaign_name_updated',
  {
    old: string;
    new: string;
  }
>;

type CampaignBudgetUpdatedEventData = CampaignHistoryData<
  'campaign_budget_updated',
  {
    budget: {
      old: number;
      new: number;
    };
    effective_at: string;
  }
>;

type CampaignCalendarUrlUpdatedEventData = CampaignHistoryData<
  'campaign_calendar_url_updated',
  {
    old: string;
    new: string;
  }
>;

type CampaignMinimalIncomeUpdatedEventData = CampaignHistoryData<
  'campaign_minimal_income_updated',
  {
    old: number;
    new: number;
  }
>;

type CampaignTargetAreaUpdatedEventData = CampaignHistoryData<
  'campaign_target_area_updated',
  {
    old: number;
    new: number;
  }
>;

type CampaignBidUpdatedEventData = CampaignHistoryData<
  'campaign_bid_updated',
  {
    old: number | number[];
    new: number | number[];
  }
>;

type CampaignIsActiveUpdatedEventData = CampaignHistoryData<
  'campaign_is_active_updated',
  {
    old: boolean;
    new: boolean;
  }
>;

type CampaignPausingReasonUpdatedEventData = CampaignHistoryData<
  'campaign_pausing_reason_updated',
  {
    old: string;
    new: string;
  }
>;

type IntegrationCreatedEventData = CampaignHistoryData<
  'integration_created',
  {
    is_active: boolean;
  }
>;

type IntegrationUpdatedEventData = CampaignHistoryData<
  'integration_updated',
  {
    is_active: {
      old: boolean;
      new: boolean;
    };
  }
>;

type IntegrationDeletedEventData = CampaignHistoryData<
  'integration_deleted',
  {
    label: string;
  }
>;

type CampaignHistoryCommonProperties = {
  type: 'campaign';
  objectId: number;
  createdAt: string;
  actor: string;
  source: LogsSource;
};

export type CampaignHistoryModel =
  | (CampaignHistoryCommonProperties &
      (
        | CampaignCreatedEventData
        | CampaignStatusUpdatedEventData
        | CampaignNameUpdatedEventData
        | CampaignBudgetUpdatedEventData
        | CampaignCalendarUrlUpdatedEventData
        | CampaignMinimalIncomeUpdatedEventData
        | CampaignTargetAreaUpdatedEventData
        | CampaignBidUpdatedEventData
        | CampaignScheduleUpdatedEventData
        | IntegrationCreatedEventData
        | IntegrationUpdatedEventData
        | IntegrationDeletedEventData
        | CampaignIsActiveUpdatedEventData
        | CampaignPausingReasonUpdatedEventData
      ))
  | TestCrmRequestEventData;

type ContractorHistoryData<TEvent extends ContractorHistoryEventTypes, TBody> = {
  eventType: TEvent;
  body: TBody;
  source: LogsSource;
};

type PromoCreatedEventData = ContractorHistoryData<'promo_created', HistoryPromoCreatedBody>;
type PromoStartedEventData = ContractorHistoryData<'promo_started', HistoryPromoStartedBody>;
type PromoFinishedEventData = ContractorHistoryData<'promo_finished', HistoryPromoFinishedBody>;

type ContractorCreatedEventData = ContractorHistoryData<
  'contractor_created',
  {
    full_name: string;
    company_name: string;
    phone_number: string;
    email: string;
    website: string | null;
    timezone: string;
    manager: string | null;
    payment_method: string;
  }
>;

type ContractorUpdatedEventData = ContractorHistoryData<
  | 'contractor_company_updated'
  | 'contractor_email_updated'
  | 'contractor_phone_updated'
  | 'contractor_time_zone_updated'
  | 'contractor_payment_method_updated'
  | 'contractor_full_name_updated',
  {
    old: string;
    new: string;
  }
>;

type ContractorSalesRepUpdatedEventData = ContractorHistoryData<
  'contractor_sales_rep_updated',
  {
    old: {
      id: number;
      fullName: string;
      email: string;
    } | null;
    new: {
      id: number;
      fullName: string;
      email: string;
    };
  }
>;

type ContractorManagerUpdatedEventData = ContractorHistoryData<
  'contractor_account_manager_updated',
  {
    old: string | null;
    new: string;
  }
>;

type ContractorWebsiteUpdatedEventData = ContractorHistoryData<
  'contractor_website_updated',
  {
    old: string | null;
    new: string | null;
  }
>;

type ContractorPasswordUpdatedEventData = ContractorHistoryData<'contractor_password_updated', {}>;

type CreditCardCreatedEventData = ContractorHistoryData<
  'credit_card_created',
  {
    contractorId: number;
    isDefault: boolean;
    last4: string;
  }
>;

type CreditCardUpdatedEventData = ContractorHistoryData<
  'credit_card_updated',
  {
    contractorId: number;
    isDefault: {
      old: boolean;
      new: boolean;
    };
    last4: string;
  }
>;

type CreditCardDeletedEventData = ContractorHistoryData<
  'credit_card_deleted',
  {
    contractorId: number;
    last4: string;
  }
>;

type ContractorEmailBillingAddedEventData = ContractorHistoryData<
  'contractor_billing_emails_added',
  {
    added_billing_emails: string[];
  }
>;

type ContractorEmailBillingDeletedEventData = ContractorHistoryData<
  'contractor_billing_emails_deleted',
  {
    deleted_billing_emails: string[];
  }
>;

type HolidayHistoryAddedEventData = ContractorHistoryData<'holiday_added', string[]>;

type HolidayHistoryDeletedEventData = ContractorHistoryData<'holiday_deleted', string[]>;

export type ContractorHistoryModel = (
  | ContractorHistoryCommonProperties
  | PromoHistoryCommonProperties
  | CreditCardHistoryCommonProperties
) &
  (
    | ContractorCreatedEventData
    | ContractorUpdatedEventData
    | ContractorManagerUpdatedEventData
    | ContractorWebsiteUpdatedEventData
    | ContractorPasswordUpdatedEventData
    | ContractorSalesRepUpdatedEventData
    | ContractorEmailBillingAddedEventData
    | ContractorEmailBillingDeletedEventData
    | PromoCreatedEventData
    | PromoStartedEventData
    | PromoFinishedEventData
    | CreditCardCreatedEventData
    | CreditCardUpdatedEventData
    | CreditCardDeletedEventData
    | HolidayHistoryAddedEventData
    | HolidayHistoryDeletedEventData
  );

type ContractorHistoryCommonProperties = {
  type: 'contractor';
  objectId: number;
  createdAt: string;
  actor: string;
};
type PromoHistoryCommonProperties = Omit<ContractorHistoryCommonProperties, 'type'> & { type: 'promo' };
type CreditCardHistoryCommonProperties = Omit<ContractorHistoryCommonProperties, 'type'> & { type: 'creditCard' };

export type CampaignScheduleUpdatedEventData = CampaignHistoryData<'campaign_schedule_updated', SchedulingUpdateModel>;

export interface SchedulingUpdateModel {
  timezone: Timezone;
  Monday: SchedulingUpdateDayModel;
  Tuesday: SchedulingUpdateDayModel;
  Wednesday: SchedulingUpdateDayModel;
  Thursday: SchedulingUpdateDayModel;
  Friday: SchedulingUpdateDayModel;
  Saturday: SchedulingUpdateDayModel;
  Sunday: SchedulingUpdateDayModel;
}

export type LogsSource = 'HomeBuddy' | 'ClientDashboard' | null;

export type HistoryGroupData = Partial<Record<keyof typeof HistoryGroupRowKey, string | number | boolean | number[]>>;

export enum HistoryGroupRowKey {
  name = 'Name',
  service = 'Service',
  trafficDays = 'Traffic days',
  trafficTime = 'Traffic time, EST',
  googleNetwork = 'Google network',
  excludedZipCodes = 'Exclude ZIP Codes',
  includedCampaigns = 'Included Campaigns',
}

export type GroupHistoryEventTypes =
  | 'group_created'
  | 'filters_applied'
  | 'group_campaigns_updated'
  | 'group_excluded_zips_updated'
  | 'group_google_network_updated'
  | 'group_name_updated'
  | 'group_traffic_days_updated'
  | 'group_traffic_time_updated';

export type GroupHistoryData<TEvent extends GroupHistoryEventTypes, TBody> = {
  eventType: TEvent;
  body: TBody;
};

export type GroupCreatedEventData = GroupHistoryData<
  'group_created',
  {
    name: string;
    service: string;
    trafficDays: string;
    trafficTime: string;
    googleNetwork: boolean;
    excludedZipCodes: number;
    includedCampaigns: string;
  }
>;

export type GroupCampaignsUpdatedEventData = GroupHistoryData<
  'group_campaigns_updated',
  {
    old: string;
    new: string;
  }
>;

export type ExcludedZipsUpdatedEventData = GroupHistoryData<
  'group_excluded_zips_updated',
  {
    old: number;
    new: number;
  }
>;

export type GoogleNetworkUpdatedEventData = GroupHistoryData<
  'group_google_network_updated',
  {
    old: boolean;
    new: boolean;
  }
>;

export type GroupNameUpdatedEventData = GroupHistoryData<
  'group_name_updated',
  {
    old: string;
    new: string;
  }
>;

export type GroupTrafficDaysUpdatedEventData = GroupHistoryData<
  'group_traffic_days_updated',
  {
    old: string;
    new: string;
  }
>;

export type GroupTrafficTimeUpdatedEventData = GroupHistoryData<
  'group_traffic_time_updated',
  {
    old: string;
    new: string;
  }
>;

export enum FilterTypeNames {
  'min_income' = 'Min Income',
  'min_coverage' = 'Min Coverage',
  'poverty_level' = 'Poverty Level',
  'owner_occupied' = 'Owner Occupied',
}

export type FiltersAppliedEventData = GroupHistoryData<
  'filters_applied',
  Record<
    keyof typeof FilterTypeNames,
    {
      old: {
        condition: SettingType['condition'];
        value: string | null;
      };
      new: {
        condition: SettingType['condition'];
        value: string | null;
      };
    }
  >
>;

type GroupHistoryCommonProperties = {
  type: 'group';
  objectId: number;
  createdAt: string;
  actor: string;
  source: LogsSource;
};

export type GroupHistoryModel = GroupHistoryCommonProperties &
  (
    | GroupCreatedEventData
    | FiltersAppliedEventData
    | GroupCampaignsUpdatedEventData
    | ExcludedZipsUpdatedEventData
    | GoogleNetworkUpdatedEventData
    | GroupNameUpdatedEventData
    | GroupTrafficDaysUpdatedEventData
    | GroupTrafficTimeUpdatedEventData
  );
